/**
 * Instructor Toolbox - Gated area for instructors
 * ==============================================
 * Features:
 * - Answer Keys for lessons
 * - Facilitation Notes & Tips
 * - Group Roster Management
 * - Teaching Resources
 * - Game Setup Guides
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast, Toaster } from 'sonner';
import { 
  BookOpen, Key, Users, Lightbulb, Gamepad2, FileText, 
  Lock, ChevronRight, Download, Eye, ArrowLeft, Search,
  GraduationCap, ClipboardList, Trophy, BookMarked, Map, Image,
  Folder, Trash2, Upload, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InstructorToolbox = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [answerKeys, setAnswerKeys] = useState([]);
  const [facilitationNotes, setFacilitationNotes] = useState([]);
  const [roster, setRoster] = useState([]);
  const [gameMaps, setGameMaps] = useState([]);
  const [bankStats, setBankStats] = useState(null);
  const [bankQuestions, setBankQuestions] = useState([]);
  const [bankFilter, setBankFilter] = useState({ game_type: '', character: '' });
  const [bankPage, setBankPage] = useState(1);
  const [bankTotal, setBankTotal] = useState(0);
  const [bankLoading, setBankLoading] = useState(false);
  const [selectedMapIdx, setSelectedMapIdx] = useState(null);
  const [managedSections, setManagedSections] = useState([]);
  const [sectionAssets, setSectionAssets] = useState([]);
  const [sectionAssetsLoading, setSectionAssetsLoading] = useState(false);
  
  // Game filtering state - which lessons have been covered
  const [coveredLessons, setCoveredLessons] = useState(() => {
    const saved = localStorage.getItem('sf_covered_lessons');
    return saved ? JSON.parse(saved) : {};
  });
  const [selectedSeries, setSelectedSeries] = useState('holiday');
  const [gameMode, setGameMode] = useState(null); // 'grinch' | 'passport' | null

  // Save covered lessons to localStorage
  useEffect(() => {
    localStorage.setItem('sf_covered_lessons', JSON.stringify(coveredLessons));
  }, [coveredLessons]);

  // Load Question Bank when its tab opens or filters/page change
  useEffect(() => {
    if (activeTab !== 'question-bank') return;
    const load = async () => {
      setBankLoading(true);
      try {
        const params = new URLSearchParams({ page: String(bankPage), per_page: '20' });
        if (bankFilter.game_type) params.append('game_type', bankFilter.game_type);
        if (bankFilter.character) params.append('character', bankFilter.character);
        const res = await fetch(`${API_URL}/api/trivia/questions/browse?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          setBankQuestions(data.questions || []);
          setBankTotal(data.total || 0);
        }
      } catch (e) { /* ignore */ }
      finally { setBankLoading(false); }
    };
    load();
  }, [activeTab, bankFilter, bankPage]);

  // Load assigned resources when a managed section opens
  const isManagedSection = (key) => managedSections.some(s => s.key === key);
  useEffect(() => {
    if (!isManagedSection(activeTab)) { setSectionAssets([]); return; }
    const token = localStorage.getItem('soul_food_token');
    const load = async () => {
      setSectionAssetsLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/instructor/toolbox/sections/${activeTab}/assets`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSectionAssets(data.assets || []);
        }
      } catch (e) { /* ignore */ }
      finally { setSectionAssetsLoading(false); }
    };
    load();
  }, [activeTab, managedSections]);

  const reloadSectionAssets = async (key) => {
    const token = localStorage.getItem('soul_food_token');
    try {
      const res = await fetch(`${API_URL}/api/instructor/toolbox/sections/${key}/assets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setSectionAssets((await res.json()).assets || []);
    } catch (e) { /* ignore */ }
  };

  const downloadAsset = async (asset) => {
    const token = localStorage.getItem('soul_food_token');
    try {
      const res = await fetch(`${API_URL}/api/instructor/toolbox/assets/${asset.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) { toast.error('Download failed'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = asset.filename || asset.label || 'download';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const uploadMyResource = async (sectionKey, file) => {
    const token = localStorage.getItem('soul_food_token');
    const fd = new FormData();
    fd.append('section_key', sectionKey);
    fd.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/instructor/toolbox/assets/upload`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
      });
      if (res.ok) { toast.success('Resource added to your class'); reloadSectionAssets(sectionKey); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Upload failed'); }
    } catch (e) { toast.error('Upload failed'); }
  };

  const deleteMyResource = async (sectionKey, assetId) => {
    const token = localStorage.getItem('soul_food_token');
    try {
      const res = await fetch(`${API_URL}/api/instructor/toolbox/assets/${assetId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Removed'); reloadSectionAssets(sectionKey); }
      else { toast.error('Could not remove'); }
    } catch (e) { toast.error('Could not remove'); }
  };

  const renderSectionAssets = (section) => {
    const mine = sectionAssets.filter(a => a.is_mine);
    const publisher = sectionAssets.filter(a => a.lane === 'publisher');
    const AssetCard = ({ a, removable }) => (
      <div className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50" data-testid={`toolbox-asset-${a.id}`}>
        <div className="w-9 h-9 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
          <FileText size={16} className="text-slate-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{a.label}</p>
          <p className="text-[11px] text-slate-400">{a.filename}</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => downloadAsset(a)} data-testid={`toolbox-asset-download-${a.id}`}>
          <Download className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Download</span>
        </Button>
        {removable && (
          <button onClick={() => deleteMyResource(section.key, a.id)} className="p-1.5 text-slate-400 hover:text-red-600" title="Remove" data-testid={`toolbox-asset-remove-${a.id}`}>
            <Trash2 size={15} />
          </button>
        )}
      </div>
    );
    return (
      <div className="space-y-5" data-testid={`section-assets-${section.key}`}>
        {/* Publisher resources */}
        <div>
          <h4 className="text-sm font-bold text-slate-700 mb-2">Resources</h4>
          {sectionAssetsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : publisher.length === 0 ? (
            <p className="text-sm text-slate-400 py-4" data-testid={`section-no-publisher-${section.key}`}>No resources have been published to this section yet.</p>
          ) : (
            <div className="space-y-2">{publisher.map(a => <AssetCard key={a.id} a={a} removable={false} />)}</div>
          )}
        </div>

        {/* Instructor's own resources */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-bold text-slate-700">My Class Resources</h4>
            <label className="inline-flex">
              <input type="file" className="hidden" data-testid={`my-resource-upload-${section.key}`}
                onChange={e => { if (e.target.files?.[0]) { uploadMyResource(section.key, e.target.files[0]); e.target.value = ''; } }} />
              <span className="inline-flex items-center cursor-pointer bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md px-3 py-1.5" data-testid={`add-my-resource-${section.key}`}>
                <Upload size={14} className="mr-1.5" /> Add my resource
              </span>
            </label>
          </div>
          <p className="text-[11px] text-slate-400 mb-2">Private to your classes — only you can see and manage these.</p>
          {mine.length === 0 ? (
            <p className="text-sm text-slate-400 py-2" data-testid={`section-no-mine-${section.key}`}>You haven't added any resources to this section.</p>
          ) : (
            <div className="space-y-2">{mine.map(a => <AssetCard key={a.id} a={a} removable={true} />)}</div>
          )}
        </div>
      </div>
    );
  };

  // Curriculum structure - all series and their lessons
  const curriculumStructure = {
    holiday: {
      name: 'Holiday Series (4 Cs)',
      lessons: [
        { id: 'hol-covenant', title: 'The Covenant', subtitle: 'God Keeps His Promises', bites: ['Bite 1: Rainbow Promise', 'Bite 2: Abraham\'s Covenant', 'Bite 3: New Covenant in Christ'] },
        { id: 'hol-cradle', title: 'The Cradle', subtitle: 'God Comes to Be with Us', bites: ['Bite 1: Prophecy Fulfilled', 'Bite 2: Immanuel', 'Bite 3: The Incarnation'] },
        { id: 'hol-cross', title: 'The Cross', subtitle: 'God Provides Salvation', bites: ['Bite 1: The Sacrifice', 'Bite 2: Substitution', 'Bite 3: It Is Finished'] },
        { id: 'hol-comforter', title: 'The Comforter', subtitle: 'God Remains with Us', bites: ['Bite 1: The Promise', 'Bite 2: Pentecost', 'Bite 3: Fruits of the Spirit'] }
      ],
      bonusLessons: [
        { id: 'hol-names-of-god', title: 'Names of God', subtitle: 'Understanding the Sacred Language' },
        { id: 'hol-times-seasons', title: 'Times and Seasons', subtitle: 'God\'s Order in Days and Numbers' }
      ]
    },
    breakfast: {
      name: 'Break*fast Series',
      lessons: [
        // Month 1 - Prayer
        { id: 'bkft-m1-l1', title: 'Hannah', subtitle: 'Prayer', month: 1 },
        { id: 'bkft-m1-l2', title: 'Solomon', subtitle: 'Wisdom', month: 1 },
        { id: 'bkft-m1-l3', title: 'Centurion', subtitle: 'Authority', month: 1 },
        { id: 'bkft-m1-l4', title: 'Chronic Woman', subtitle: 'Persistence', month: 1 },
        // Month 2 - Through
        { id: 'bkft-m2-l1', title: 'Joseph', subtitle: 'Forgiveness', month: 2 },
        { id: 'bkft-m2-l2', title: 'Abram', subtitle: 'Trust', month: 2 },
        { id: 'bkft-m2-l3', title: 'Rahab', subtitle: 'Courage', month: 2 },
        { id: 'bkft-m2-l4', title: 'Esther', subtitle: 'Purpose', month: 2 },
        // Month 3 - Worship
        { id: 'bkft-m3-l1', title: 'David', subtitle: 'Worship', month: 3 },
        { id: 'bkft-m3-l2', title: 'Mary', subtitle: 'Surrender', month: 3 },
        { id: 'bkft-m3-l3', title: 'Paul & Silas', subtitle: 'Joy in Trials', month: 3 },
        { id: 'bkft-m3-l4', title: 'The Leper', subtitle: 'Gratitude', month: 3 }
      ]
    }
  };

  // Toggle lesson coverage
  const toggleLessonCovered = (seriesId, lessonId) => {
    setCoveredLessons(prev => {
      const seriesKey = `${seriesId}`;
      const current = prev[seriesKey] || [];
      if (current.includes(lessonId)) {
        return { ...prev, [seriesKey]: current.filter(id => id !== lessonId) };
      } else {
        return { ...prev, [seriesKey]: [...current, lessonId] };
      }
    });
  };

  // Check if lesson is covered
  const isLessonCovered = (seriesId, lessonId) => {
    return (coveredLessons[seriesId] || []).includes(lessonId);
  };

  // Get count of covered lessons for a series
  const getCoveredCount = (seriesId) => {
    return (coveredLessons[seriesId] || []).length;
  };

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('soul_food_token');
      const userData = localStorage.getItem('soul_food_user');
      
      if (!token || !userData) {
        navigate('/login', { state: { returnTo: '/instructor-toolbox', message: 'Please sign in with an Instructor account to access the Toolbox.' } });
        return;
      }

      try {
        const parsedUser = JSON.parse(userData);
        
        // Check if user has instructor access
        const instructorRoles = ['instructor', 'instructor_tester', 'admin', 'owner'];
        const hasAccess = instructorRoles.includes(parsedUser.role) || 
                          parsedUser.access_level === 'instructor' ||
                          parsedUser.permissions?.includes('view_instructor_content');
        
        if (!hasAccess) {
          toast.error('Instructor access required');
          navigate('/', { state: { message: 'Instructor Edition access required for the Toolbox.' } });
          return;
        }

        setUser(parsedUser);
        await loadInstructorContent(token);
      } catch (err) {
        console.error('Auth check failed:', err);
        navigate('/login', { state: { returnTo: '/instructor-toolbox' } });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const loadInstructorContent = async (token) => {
    try {
      // Load answer keys
      const keysRes = await fetch(`${API_URL}/api/instructor/answer-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (keysRes.ok) {
        const keysData = await keysRes.json();
        setAnswerKeys(keysData.items || []);
      }

      // Load facilitation notes
      const notesRes = await fetch(`${API_URL}/api/instructor/facilitation-notes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (notesRes.ok) {
        const notesData = await notesRes.json();
        setFacilitationNotes(notesData.items || []);
      }

      // Load roster
      const rosterRes = await fetch(`${API_URL}/api/instructor/roster`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (rosterRes.ok) {
        const rosterData = await rosterRes.json();
        setRoster(rosterData.members || []);
      }

      // Load game maps and assets
      try {
        const mapsRes = await fetch(`${API_URL}/api/trivia/game-assets`);
        if (mapsRes.ok) {
          const mapsData = await mapsRes.json();
          setGameMaps(mapsData.assets || []);
        }
      } catch (e) { /* maps load skipped */ }

      // Load question bank stats
      try {
        const statsRes = await fetch(`${API_URL}/api/trivia/bank/stats`);
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setBankStats(statsData);
        }
      } catch (e) { /* stats load skipped */ }

      // Load admin-managed toolbox sections (data-driven)
      try {
        const secRes = await fetch(`${API_URL}/api/instructor/toolbox/sections`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (secRes.ok) {
          const secData = await secRes.json();
          setManagedSections(secData.sections || []);
        }
      } catch (e) { /* sections load skipped */ }
    } catch (err) {
      console.error('Failed to load instructor content:', err);
    }
  };

  // Toolbox sections are now ADMIN-MANAGED (data-driven). We render the
  // admin-configured sections from the API, then append the two built-in
  // functional tools (Game Setup wizard, Achievement Awards). Audio stays in
  // My Library, not here.
  const SECTION_ICONS = { Map, Key, BookMarked, Gamepad2, FileText, Lightbulb, Folder, Image, Trophy, Users, ClipboardList };
  const SECTION_COLORS = ['bg-teal-500', 'bg-emerald-500', 'bg-indigo-500', 'bg-cyan-500', 'bg-rose-500', 'bg-amber-500', 'bg-sky-500', 'bg-fuchsia-500'];
  const toolboxSections = [
    ...managedSections.map((s, i) => ({
      id: s.key,
      title: s.title,
      icon: SECTION_ICONS[s.icon] || Folder,
      description: s.description,
      color: SECTION_COLORS[i % SECTION_COLORS.length],
      special_view: s.special_view,
      badge: s.key === 'maps' && gameMaps.length ? `${gameMaps.length} Maps`
        : s.key === 'question-banks' && bankStats?.total_questions ? `${bankStats.total_questions} Questions`
        : null
    })),
    {
      id: 'games',
      title: 'Game Setup',
      icon: Gamepad2,
      description: 'How to run Grid Iron Challenge and Passport Trek with your group',
      color: 'bg-purple-500'
    },
    {
      id: 'certificates',
      title: 'Achievement Awards',
      icon: Trophy,
      description: 'Order achievement medallions for your group (printable certificates coming soon)',
      color: 'bg-orange-500'
    }
  ];

  // Sample answer keys data (these would come from the API)
  const sampleAnswerKeys = [
    { id: 'bkft-m1-l1', series: 'Breakfast', module: 'Month 1', lesson: 'Hannah - Prayer', available: true },
    { id: 'bkft-m1-l2', series: 'Breakfast', module: 'Month 1', lesson: 'Solomon - Wisdom', available: true },
    { id: 'bkft-m1-l3', series: 'Breakfast', module: 'Month 1', lesson: 'Centurion - Authority', available: true },
    { id: 'bkft-m1-l4', series: 'Breakfast', module: 'Month 1', lesson: 'Chronic Woman - Persistence', available: true },
    { id: 'bkft-m2-l1', series: 'Breakfast', module: 'Month 2', lesson: 'Joseph - Forgiveness', available: true },
    { id: 'bkft-m2-l2', series: 'Breakfast', module: 'Month 2', lesson: 'Abram - Trust', available: true },
    { id: 'bkft-m2-l3', series: 'Breakfast', module: 'Month 2', lesson: 'Rahab - Courage', available: true },
    { id: 'bkft-m2-l4', series: 'Breakfast', module: 'Month 2', lesson: 'Esther - Purpose', available: true },
    { id: 'hol-l1', series: 'Holiday', module: '4Cs', lesson: 'Covenant - Abraham', available: true },
    { id: 'hol-l2', series: 'Holiday', module: '4Cs', lesson: 'Cradle - Nativity', available: true },
    { id: 'hol-l3', series: 'Holiday', module: '4Cs', lesson: 'Cross - Redemption', available: true },
    { id: 'hol-l4', series: 'Holiday', module: '4Cs', lesson: 'Comforter - Holy Spirit', available: true }
  ];

  // Sample facilitation notes
  const sampleFacilitationNotes = [
    { id: 'fn-opening', title: 'Opening Your Session', type: 'general', description: 'Ice breakers and prayer starters' },
    { id: 'fn-discussion', title: 'Leading Discussions', type: 'general', description: 'Encouraging participation without lectures' },
    { id: 'fn-activities', title: 'Group Activities Guide', type: 'general', description: 'Interactive exercises for each lesson' },
    { id: 'fn-prayer', title: 'Prayer Module Tips', type: 'breakfast-m1', description: 'Month 1 - Prayer theme teaching notes' },
    { id: 'fn-through', title: 'Through Module Tips', type: 'breakfast-m2', description: 'Month 2 - Perseverance theme notes' },
    { id: 'fn-closing', title: 'Closing Strong', type: 'general', description: 'Recap and takeaway assignments' }
  ];

  // Offline game files are defined inline in the offlineGameFiles array above

  const renderCertificates = () => (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="certificates-preview-banner">
        <strong>Preview only.</strong> Printable certificate generator is not yet wired. Medallion ordering is fully live.
      </div>
      <p className="text-slate-600">Recognize your group members' achievements with official certificates and medallions.</p>
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-orange-500" />Printable Certificates</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">Download and print achievement certificates for your group members.</p>
            <div className="space-y-2">
              {['Lesson Completion Certificate', 'Series Completion Certificate', 'Game Champion Certificate'].map(c => (
                <Button key={c} variant="outline" className="w-full justify-between" disabled title="Not yet wired" data-testid={`cert-${c.replace(/\s+/g, '-').toLowerCase()}`}>
                  <span className="flex items-center"><Download className="w-4 h-4 mr-2" />{c}</span>
                  <Badge variant="outline" className="text-[10px] text-slate-500">Preview</Badge>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-orange-500" />Achievement Medallions</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">Order metal medallions to award outstanding achievement.</p>
            <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
              <p className="text-sm font-medium text-orange-800">Bulk Discounts Available!</p>
              <p className="text-xs text-orange-600 mt-1">Teacher Pack (3) - $24.99 | Ministry Pack (10) - $69.99</p>
            </div>
            <Button className="w-full bg-orange-500 hover:bg-orange-600" onClick={() => navigate('/quick-order')} data-testid="order-medallions-btn">Order Medallions</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderMaps = () => (
    <div className="space-y-6" data-testid="maps-section">
      <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
        <h4 className="font-bold text-teal-800 mb-1">Biblical Maps &amp; Visual Aids</h4>
        <p className="text-sm text-teal-600">Use in bonus rounds, daily doubles, classroom projection, or print for handouts.</p>
      </div>
      
      {selectedMapIdx !== null && gameMaps[selectedMapIdx] ? (
        <div className="space-y-4">
          <Button variant="ghost" onClick={() => setSelectedMapIdx(null)} className="text-slate-600">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to All Maps
          </Button>
          <Card className="overflow-hidden">
            <img 
              src={`${API_URL}/api/content/images/${gameMaps[selectedMapIdx].file_path.split('/images/')[1]}`} 
              alt={gameMaps[selectedMapIdx].name}
              className="w-full max-h-[600px] object-contain bg-slate-100"
              data-testid="map-full-image"
            />
            <CardContent className="p-4 space-y-2">
              <h3 className="text-lg font-bold text-slate-800">{gameMaps[selectedMapIdx].name}</h3>
              <p className="text-sm text-slate-600">{gameMaps[selectedMapIdx].description}</p>
              {gameMaps[selectedMapIdx].scripture_refs && (
                <div className="flex flex-wrap gap-1">
                  {gameMaps[selectedMapIdx].scripture_refs.map((ref, i) => (
                    <Badge key={`ref-${ref}`} variant="outline" className="text-xs">{ref}</Badge>
                  ))}
                </div>
              )}
              {gameMaps[selectedMapIdx].characters && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {gameMaps[selectedMapIdx].characters.map((ch, i) => (
                    <Badge key={`ch-${ch}`} className="bg-teal-100 text-teal-700 text-xs">{ch}</Badge>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-slate-400 italic border-t pt-2 mt-3">
                {gameMaps[selectedMapIdx].credit || 'Map credit: Joe Anderson, headwatersresources.org (CC BY-NC-SA 4.0)'}
              </p>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {gameMaps.map((mapAsset, idx) => (
            <Card 
              key={mapAsset.id || mapAsset.title || `gmap-${idx}`} 
              className="overflow-hidden hover:shadow-lg transition-all cursor-pointer border-2 hover:border-teal-300"
              onClick={() => setSelectedMapIdx(idx)}
              data-testid={`map-card-${idx}`}
            >
              <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
                <img 
                  src={`${API_URL}/api/content/images/${mapAsset.file_path.split('/images/')[1]}`}
                  alt={mapAsset.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {mapAsset.use_case?.includes('daily_double') && (
                  <Badge className="absolute top-2 right-2 bg-amber-500 text-white text-[10px]">Daily Double</Badge>
                )}
              </div>
              <CardContent className="p-3">
                <p className="font-semibold text-slate-800 text-sm leading-tight">{mapAsset.name}</p>
                {mapAsset.characters && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {mapAsset.characters.slice(0, 3).map((ch, i) => (
                      <Badge key={`mc-${ch}`} variant="outline" className="text-[10px]">{ch}</Badge>
                    ))}
                  </div>
                )}
                <p className="text-[9px] text-slate-400 italic mt-2">
                  {mapAsset.credit || 'Joe Anderson / headwatersresources.org'}
                </p>
              </CardContent>
            </Card>
          ))}
          {gameMaps.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-400">
              <Map className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No maps loaded yet.</p>
            </div>
          )}
        </div>
      )}
      
      {/* Credit footnote */}
      <div className="border-t pt-3 mt-4">
        <p className="text-[10px] text-slate-400 leading-relaxed">
          Maps by Joe Anderson, <a href="https://headwatersresources.org" target="_blank" rel="noopener noreferrer" className="underline">headwatersresources.org</a> (CC BY-NC-SA 4.0). 
          Used under license for educational and ministry purposes. Passion Week map sourced from Overflow Harvest LLC curriculum archives.
        </p>
      </div>
    </div>
  );

  const offlineGameFiles = [
    {
      id: 'grinch-game-pk',
      title: 'Grid Iron Challenge — Game Pack',
      subtitle: 'Full instructions, question banks, 6 Grid Iron bingo card variants (A-1, A-2, B-1, B-2, C, C-2), stamp rules & trackers',
      file: 'ie-grinch-bingo-game-pk.pdf',
      pages: '23 pages',
      color: 'purple',
      icon: Gamepad2,
      thumb: '/covers/game-gridiron-ae.png'
    },
    {
      id: 'grinch-card-pk',
      title: 'Grid Iron Challenge — Bingo Cards',
      subtitle: 'Printable Grid Iron Cards (A-1, A-2, B-1) + tracker templates',
      file: 'ie-grinch-bingo-card-pk.pdf',
      pages: '4 pages',
      color: 'purple',
      icon: ClipboardList,
      thumb: '/covers/game-gridiron-ye.png'
    },
    {
      id: 'passport-trek',
      title: 'Passport Trek Game',
      subtitle: 'Stamp collection game — instructions, challenge boxes, leader key, tracking sheets',
      file: 'ie-passport-trek-game.pdf',
      pages: '10 pages',
      color: 'blue',
      icon: GraduationCap,
      thumb: '/covers/game-passport-ae.png'
    }
  ];

  const renderGamePacks = () => (
    <div className="space-y-6" data-testid="game-packs-section">
      <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-200">
        <h4 className="font-bold text-cyan-800 mb-1">Offline Game Materials — Print & Play</h4>
        <p className="text-sm text-cyan-600">
          Download and print these materials to run Grid Iron Challenge bingo rounds, Passport Trek stamp challenges, and classroom review sessions with your group.
        </p>
        <div className="flex flex-wrap gap-3 mt-3">
          <Badge className="bg-purple-100 text-purple-700">Grid Iron Bingo</Badge>
          <Badge className="bg-blue-100 text-blue-700">Passport Trek</Badge>
          <Badge className="bg-teal-100 text-teal-700">Maps Reference</Badge>
        </div>
      </div>

      <div className="grid gap-4">
        {offlineGameFiles.map(item => {
          const colorMap = {
            purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'bg-purple-500', title: 'text-purple-800', badge: 'bg-purple-100 text-purple-700' },
            blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'bg-blue-500', title: 'text-blue-800', badge: 'bg-blue-100 text-blue-700' },
            teal: { bg: 'bg-teal-50', border: 'border-teal-200', icon: 'bg-teal-500', title: 'text-teal-800', badge: 'bg-teal-100 text-teal-700' }
          };
          const c = colorMap[item.color];
          const IconComp = item.icon;
          return (
            <Card key={item.id} className={`${c.border} border-2 hover:shadow-md transition-shadow`} data-testid={`game-file-${item.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                {item.thumb ? (
                  <img
                    src={item.thumb}
                    alt={`${item.title} thumbnail`}
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover flex-shrink-0 border border-slate-200 bg-white"
                    data-testid={`thumb-${item.id}`}
                  />
                ) : (
                  <div className={`w-12 h-12 rounded-xl ${c.icon} flex items-center justify-center flex-shrink-0`}>
                    <IconComp className="w-6 h-6 text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className={`font-bold text-sm sm:text-base ${c.title}`}>{item.title}</h4>
                  <p className="text-xs sm:text-sm text-slate-600 mt-0.5">{item.subtitle}</p>
                  <Badge variant="outline" className="mt-1.5 text-[10px]">{item.pages}</Badge>
                </div>
                <Button
                  size="sm"
                  className={`${c.icon} text-white flex-shrink-0`}
                  data-testid={`download-${item.id}`}
                  onClick={() => {
                    window.open(`${API_URL}/api/content/games/${item.file}`, '_blank', 'noopener,noreferrer');
                    toast.success(`Downloading ${item.title}...`);
                  }}
                >
                  <Download className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Download</span>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  const renderQuestionBank = () => {
    const gameTypeLabels = {
      trivia_testament: 'Trivia Testament',
      tricky_trivia: 'Tricky Trivia',
      who_am_i: 'Who Am I?',
      deep_cut: 'Deep Cut'
    };
    const totalPages = Math.max(1, Math.ceil(bankTotal / 20));
    return (
      <div className="space-y-6" data-testid="question-bank-section">
        <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
          <h4 className="font-bold text-indigo-800 mb-1">Question Bank</h4>
          <p className="text-sm text-indigo-600">
            Trivia and Jeopardy-style questions parsed from across the lessons. These power Grid Iron Challenge,
            Passport Trek, and the online review games. Browse, filter, and reference them when planning sessions.
          </p>
        </div>

        {bankStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bank-stats">
            <div className="bg-white p-3 rounded-lg border text-center">
              <p className="text-2xl font-bold text-indigo-600">{bankStats.total_questions}</p>
              <p className="text-[11px] text-slate-500">Total Questions</p>
            </div>
            {Object.entries(bankStats.by_game_type || {}).map(([type, count]) => (
              <div key={type} className="bg-white p-3 rounded-lg border text-center">
                <p className="text-2xl font-bold text-purple-600">{count}</p>
                <p className="text-[11px] text-slate-500">{gameTypeLabels[type] || type}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 items-center">
          <select
            className="border rounded-lg p-2 text-sm bg-white"
            value={bankFilter.game_type}
            onChange={(e) => { setBankPage(1); setBankFilter(f => ({ ...f, game_type: e.target.value })); }}
            data-testid="bank-gametype-filter"
          >
            <option value="">All Game Types</option>
            {Object.keys(bankStats?.by_game_type || {}).map(t => (
              <option key={t} value={t}>{gameTypeLabels[t] || t}</option>
            ))}
          </select>
          <select
            className="border rounded-lg p-2 text-sm bg-white"
            value={bankFilter.character}
            onChange={(e) => { setBankPage(1); setBankFilter(f => ({ ...f, character: e.target.value })); }}
            data-testid="bank-character-filter"
          >
            <option value="">All Characters</option>
            {Object.keys(bankStats?.by_character || {}).map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {(bankFilter.game_type || bankFilter.character) && (
            <Button size="sm" variant="ghost" onClick={() => { setBankPage(1); setBankFilter({ game_type: '', character: '' }); }} data-testid="bank-clear-filters">Clear</Button>
          )}
        </div>

        {bankLoading ? (
          <div className="text-center py-10 text-slate-400" data-testid="bank-loading">Loading questions…</div>
        ) : bankQuestions.length === 0 ? (
          <div className="text-center py-10 text-slate-400" data-testid="bank-empty">No questions match these filters.</div>
        ) : (
          <div className="space-y-3" data-testid="bank-question-list">
            {bankQuestions.map((q, idx) => (
              <Card key={q.qid || idx} className="border hover:shadow-sm transition-shadow" data-testid={`bank-question-${q.qid || idx}`}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    {q.character && <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">{q.character}</Badge>}
                    {q.game_type && <Badge variant="outline" className="text-[10px]">{gameTypeLabels[q.game_type] || q.game_type}</Badge>}
                    {q.tier ? <Badge variant="outline" className="text-[10px]">{q.tier} pts</Badge> : null}
                    {q.difficulty && <Badge variant="outline" className="text-[10px] capitalize">{q.difficulty}</Badge>}
                  </div>
                  <p className="font-medium text-slate-800 text-sm">{q.question}</p>
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <Key className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                    <span className="text-emerald-700 font-semibold">{q.correct_answer}</span>
                  </div>
                  {q.scripture && <p className="text-xs text-slate-400 mt-1 italic">{q.scripture}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {bankTotal > 20 && (
          <div className="flex items-center justify-between pt-2">
            <Button size="sm" variant="outline" disabled={bankPage <= 1} onClick={() => setBankPage(p => Math.max(1, p - 1))} data-testid="bank-prev-page">Previous</Button>
            <span className="text-sm text-slate-500">Page {bankPage} of {totalPages}</span>
            <Button size="sm" variant="outline" disabled={bankPage >= totalPages} onClick={() => setBankPage(p => p + 1)} data-testid="bank-next-page">Next</Button>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    // Managed (admin-configured) section: render its built-in special view (if
    // any) plus the assigned publisher + instructor resources.
    const section = managedSections.find(s => s.key === activeTab);
    if (section) {
      return (
        <div className="space-y-6">
          {section.special_view === 'maps_gallery' && renderMaps()}
          {section.special_view === 'question_bank' && renderQuestionBank()}
          {section.special_view === 'offline_games' && renderGamePacks()}
          {renderSectionAssets(section)}
        </div>
      );
    }
    switch (activeTab) {
      case 'answer-keys':
        return (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="answer-keys-preview-banner">
              <strong>Preview only.</strong> Answer-key PDFs are not wired in this build. Buttons are disabled until uploads are linked.
            </div>
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Search answer keys..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="grid gap-3">
              {sampleAnswerKeys
                .filter(key => 
                  key.lesson.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  key.series.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map(key => (
                <Card key={key.id} className="opacity-75">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Key className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{key.lesson}</p>
                        <p className="text-sm text-slate-500">{key.series} • {key.module}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs text-slate-500">Preview</Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled
                        title="Not yet wired"
                        data-testid={`answer-key-view-${key.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        disabled
                        title="Not yet wired"
                        data-testid={`answer-key-pdf-${key.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'facilitation':
        return (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="facilitation-preview-banner">
              <strong>Preview only.</strong> Facilitation notes are not yet uploaded — cards are read-only.
            </div>
            <p className="text-slate-600 mb-6">
              Practical guides to help you lead engaging and meaningful study sessions.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {sampleFacilitationNotes.map(note => (
                <Card key={note.id} className="opacity-75 cursor-not-allowed">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Lightbulb className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-semibold text-slate-800">{note.title}</p>
                          <Badge variant="outline" className="text-[10px] text-slate-500">Preview</Badge>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{note.description}</p>
                        <Badge variant="outline" className="mt-2 text-xs">
                          {note.type === 'general' ? 'All Lessons' : note.type}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'roster':
        return (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="roster-preview-banner">
              <strong>Preview only.</strong> Member management is read-only in this build.
            </div>
            <div className="flex items-center justify-between mb-6">
              <p className="text-slate-600">
                Track your study group members and their progress.
              </p>
              <Button disabled title="Not yet wired" data-testid="roster-add-member-btn">
                <Users className="w-4 h-4 mr-2" />
                Add Member
              </Button>
            </div>

            {roster.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="font-semibold text-slate-700 mb-2">No Members Yet</h3>
                  <p className="text-slate-500 mb-4">
                    Member sync is not yet connected. This panel will populate once roster ingestion is wired.
                  </p>
                  <Button disabled title="Not yet wired">
                    Add Your First Member
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {roster.map(member => (
                  <Card key={member.id}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="font-bold text-blue-600">{member.name?.[0] || '?'}</span>
                        </div>
                        <div>
                          <p className="font-semibold">{member.name}</p>
                          <p className="text-sm text-slate-500">{member.email}</p>
                        </div>
                      </div>
                      <Badge variant="outline">{member.progress || '0%'} Complete</Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 'games':
        return (
          <div className="space-y-6">
            {!gameMode ? (
              <>
                {/* Step 1: Select Lessons Covered */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 sm:p-5 border border-indigo-200">
                  <h4 className="font-bold text-base sm:text-lg text-indigo-800 mb-2 flex items-center gap-2">
                    <span>📚</span> Step 1: Select Lessons You've Covered
                  </h4>
                  <p className="text-xs sm:text-sm text-indigo-600 mb-4">
                    Games will only pull questions from lessons you've marked as covered.
                  </p>
                  
                  {/* Series Selector - Stack on mobile */}
                  <div className="flex flex-col sm:flex-row gap-2 mb-4">
                    {Object.entries(curriculumStructure).map(([key, series]) => (
                      <Button
                        key={key}
                        variant={selectedSeries === key ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedSeries(key)}
                        className={`justify-between sm:justify-center ${selectedSeries === key ? "bg-indigo-600" : ""}`}
                      >
                        <span className="truncate">{series.name}</span>
                        <Badge className="ml-2 bg-white/20 flex-shrink-0">{getCoveredCount(key)}/{series.lessons.length}</Badge>
                      </Button>
                    ))}
                  </div>

                  {/* Lessons Checklist */}
                  <div className="bg-white rounded-lg p-3 sm:p-4 max-h-64 overflow-y-auto">
                    <div className="space-y-2">
                      {curriculumStructure[selectedSeries]?.lessons.map(lesson => (
                        <label 
                          key={lesson.id}
                          className={`flex items-start sm:items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            isLessonCovered(selectedSeries, lesson.id) 
                              ? 'bg-green-50 border border-green-200' 
                              : 'hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isLessonCovered(selectedSeries, lesson.id)}
                            onChange={() => toggleLessonCovered(selectedSeries, lesson.id)}
                            className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500 mt-0.5 sm:mt-0 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-slate-800 text-sm sm:text-base">{lesson.title}</span>
                            <span className="text-slate-500 text-xs sm:text-sm block sm:inline sm:ml-2">— {lesson.subtitle}</span>
                            {lesson.month && (
                              <Badge variant="outline" className="ml-0 sm:ml-2 mt-1 sm:mt-0 text-xs">Month {lesson.month}</Badge>
                            )}
                          </div>
                          {isLessonCovered(selectedSeries, lesson.id) && (
                            <span className="text-green-500 text-lg">✓</span>
                          )}
                        </label>
                      ))}
                      
                      {/* Bonus Lessons */}
                      {curriculumStructure[selectedSeries]?.bonusLessons && (
                        <>
                          <div className="border-t pt-2 mt-3">
                            <p className="text-xs font-medium text-slate-500 uppercase mb-2">Bonus Lessons</p>
                          </div>
                          {curriculumStructure[selectedSeries].bonusLessons.map(lesson => (
                            <label 
                              key={lesson.id}
                              className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                isLessonCovered(selectedSeries, lesson.id) 
                                  ? 'bg-amber-50 border border-amber-200' 
                                  : 'hover:bg-slate-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isLessonCovered(selectedSeries, lesson.id)}
                                onChange={() => toggleLessonCovered(selectedSeries, lesson.id)}
                                className="w-5 h-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                              />
                              <div className="flex-1">
                                <span className="font-medium text-slate-800">{lesson.title}</span>
                                <span className="text-slate-500 text-sm ml-2">— {lesson.subtitle}</span>
                                <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">Bonus</Badge>
                              </div>
                              {isLessonCovered(selectedSeries, lesson.id) && (
                                <span className="text-amber-500 text-lg">✓</span>
                              )}
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Coverage Summary */}
                  <div className="mt-3 flex items-center justify-between">
                    <p className="text-sm text-indigo-700">
                      <strong>{getCoveredCount(selectedSeries)}</strong> of {curriculumStructure[selectedSeries]?.lessons.length} lessons covered
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const series = curriculumStructure[selectedSeries];
                        const allIds = [...series.lessons.map(l => l.id), ...(series.bonusLessons?.map(l => l.id) || [])];
                        setCoveredLessons(prev => ({ ...prev, [selectedSeries]: allIds }));
                        toast.success('All lessons marked as covered');
                      }}
                    >
                      Mark All Covered
                    </Button>
                  </div>
                </div>

                {/* Step 2: Choose Game */}
                <div>
                  <h4 className="font-bold text-base sm:text-lg text-slate-800 mb-4 flex items-center gap-2">
                    <span>🎮</span> Step 2: Choose Your Game
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {/* GRinCH Card */}
                    <Card className="border-2 border-purple-200 hover:border-purple-400 transition-colors cursor-pointer active:scale-[0.98] overflow-hidden" onClick={() => setGameMode('grinch')} data-testid="grinch-game-card">
                      <div className="h-28 sm:h-32 bg-purple-100 flex items-center justify-center overflow-hidden">
                        <img src="/covers/game-gridiron-ae.png" alt="Grid Iron Challenge" className="h-full w-auto object-contain" />
                      </div>
                      <CardHeader className="bg-purple-50 p-3 sm:p-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-purple-500 flex items-center justify-center flex-shrink-0">
                            <Gamepad2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-purple-800 text-base sm:text-lg">Grid Iron Challenge</CardTitle>
                            <CardDescription className="text-xs sm:text-sm">GRinCH — Print &amp; Play</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                        <p className="text-xs sm:text-sm text-slate-600">
                          Team-based trivia game. Perfect for competitive review sessions.
                        </p>
                        <ul className="text-xs sm:text-sm space-y-1">
                          <li className="flex items-center gap-2"><span className="text-green-500">✓</span> 2-4 teams</li>
                          <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Point multipliers</li>
                          <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Championship rounds</li>
                        </ul>
                        <div className="pt-2 border-t">
                          <p className="text-xs text-purple-600">
                            {getCoveredCount(selectedSeries) > 0 
                              ? `✅ ${getCoveredCount(selectedSeries)} lessons available`
                              : '⚠️ Select lessons above first'}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Passport Trek Card */}
                    <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors cursor-pointer active:scale-[0.98] overflow-hidden" onClick={() => setGameMode('passport')} data-testid="passport-game-card">
                      <div className="h-28 sm:h-32 bg-blue-100 flex items-center justify-center overflow-hidden">
                        <img src="/covers/game-passport-ae.png" alt="Passport Trek" className="h-full w-auto object-contain" />
                      </div>
                      <CardHeader className="bg-blue-50 p-3 sm:p-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
                            <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-blue-800 text-base sm:text-lg">Passport Trek</CardTitle>
                            <CardDescription className="text-xs sm:text-sm">Stamp Collection — Print &amp; Play</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                        <p className="text-xs sm:text-sm text-slate-600">
                          Individual progress tracking with stamp collection rewards.
                        </p>
                        <ul className="text-xs sm:text-sm space-y-1">
                          <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Individual or team mode</li>
                          <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Stamp collection</li>
                          <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Completion certificates</li>
                        </ul>
                        <div className="pt-2 border-t">
                          <p className="text-xs text-blue-600">
                            {getCoveredCount(selectedSeries) > 0 
                              ? `✅ ${getCoveredCount(selectedSeries)} lessons available`
                              : '⚠️ Select lessons above first'}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Pro Tip */}
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-3 sm:p-4 flex items-start sm:items-center gap-3 sm:gap-4">
                    <Lightbulb className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600 flex-shrink-0 mt-0.5 sm:mt-0" />
                    <div>
                      <p className="font-semibold text-amber-800 text-sm sm:text-base">Pro Tip: Game Session Timing</p>
                      <p className="text-xs sm:text-sm text-amber-700">
                        Schedule games for the last 15-20 minutes of your session as a reward for completing the lesson.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              /* Game Configuration Screen */
              <div className="space-y-4 sm:space-y-6">
                <Button variant="ghost" onClick={() => setGameMode(null)} className="mb-2 sm:mb-4 text-sm">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Game Selection
                </Button>

                {gameMode === 'grinch' && (
                  <div className="space-y-4 sm:space-y-6">
                    <div className="bg-purple-50 rounded-xl p-4 sm:p-6 border border-purple-200">
                      <div className="flex items-center gap-3 sm:gap-4 mb-4">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-purple-500 flex items-center justify-center flex-shrink-0">
                          <Gamepad2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl sm:text-2xl font-bold text-purple-800">Grid Iron Challenge Setup</h3>
                          <p className="text-purple-600 text-sm sm:text-base">Offline team trivia — print, run, score</p>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-3 sm:p-4 mb-4">
                        <h4 className="font-semibold text-slate-800 mb-2 text-sm sm:text-base">Questions will be drawn from:</h4>
                        <div className="flex flex-wrap gap-2">
                          {(coveredLessons[selectedSeries] || []).map(lessonId => {
                            const lesson = [...(curriculumStructure[selectedSeries]?.lessons || []), ...(curriculumStructure[selectedSeries]?.bonusLessons || [])].find(l => l.id === lessonId);
                            return lesson ? (
                              <Badge key={lessonId} className="bg-purple-100 text-purple-700">
                                {lesson.title}
                              </Badge>
                            ) : null;
                          })}
                          {(coveredLessons[selectedSeries] || []).length === 0 && (
                            <p className="text-slate-500 text-sm">No lessons selected. Go back and mark covered lessons.</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Number of Teams</label>
                          <select className="w-full border rounded-lg p-2 text-sm sm:text-base">
                            <option>2 Teams</option>
                            <option>3 Teams</option>
                            <option>4 Teams</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Question Count</label>
                          <select className="w-full border rounded-lg p-2 text-sm sm:text-base">
                            <option>10 Questions (Quick)</option>
                            <option>20 Questions (Standard)</option>
                            <option>30 Questions (Extended)</option>
                          </select>
                        </div>
                      </div>

                      <Button 
                        className="w-full bg-purple-600 hover:bg-purple-700 py-4 sm:py-6 text-base sm:text-lg"
                        onClick={() => {
                          if ((coveredLessons[selectedSeries] || []).length === 0) {
                            toast.error('Please select at least one covered lesson first');
                            return;
                          }
                          setActiveTab('game-packs');
                          toast.success('Opening printable Grid Iron Challenge materials…');
                        }}
                        data-testid="grinch-open-pack-btn"
                      >
                        <Gamepad2 className="w-5 h-5 mr-2" />
                        Open Print Materials
                      </Button>
                    </div>
                  </div>
                )}

                {gameMode === 'passport' && (
                  <div className="space-y-4 sm:space-y-6">
                    <div className="bg-blue-50 rounded-xl p-4 sm:p-6 border border-blue-200">
                      <div className="flex items-center gap-3 sm:gap-4 mb-4">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                          <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl sm:text-2xl font-bold text-blue-800">Passport Trek Setup</h3>
                          <p className="text-blue-600 text-sm sm:text-base">Stamp Collection Challenge</p>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-3 sm:p-4 mb-4">
                        <h4 className="font-semibold text-slate-800 mb-2 text-sm sm:text-base">Available Stamps (Covered Lessons):</h4>
                        <div className="flex flex-wrap gap-2">
                          {(coveredLessons[selectedSeries] || []).map(lessonId => {
                            const lesson = [...(curriculumStructure[selectedSeries]?.lessons || []), ...(curriculumStructure[selectedSeries]?.bonusLessons || [])].find(l => l.id === lessonId);
                            return lesson ? (
                              <Badge key={lessonId} className="bg-blue-100 text-blue-700 text-xs sm:text-sm">
                                🎫 {lesson.title}
                              </Badge>
                            ) : null;
                          })}
                          {(coveredLessons[selectedSeries] || []).length === 0 && (
                            <p className="text-slate-500 text-xs sm:text-sm">No lessons selected. Go back and mark covered lessons.</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Game Mode</label>
                          <select className="w-full border rounded-lg p-2 text-sm sm:text-base">
                            <option>Individual (Each person)</option>
                            <option>Team Race (Groups compete)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Challenges per Stamp</label>
                          <select className="w-full border rounded-lg p-2 text-sm sm:text-base">
                            <option>1 Challenge (Quick)</option>
                            <option>3 Challenges (Standard)</option>
                            <option>6 Challenges (Full)</option>
                          </select>
                        </div>
                      </div>

                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700 py-4 sm:py-6 text-base sm:text-lg"
                        onClick={() => {
                          if ((coveredLessons[selectedSeries] || []).length === 0) {
                            toast.error('Please select at least one covered lesson first');
                            return;
                          }
                          setActiveTab('game-packs');
                          toast.success('Opening printable Passport Trek materials…');
                        }}
                        data-testid="passport-open-pack-btn"
                      >
                        <GraduationCap className="w-5 h-5 mr-2" />
                        Open Print Materials
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'resources':
        return (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="resources-preview-banner">
              <strong>Preview only.</strong> Teaching resource downloads are not yet wired — cards are read-only.
            </div>
            <p className="text-slate-600 text-sm sm:text-base mb-4 sm:mb-6">
              Downloadable materials to enhance your teaching sessions.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              {[
                { title: 'Lesson Slides Template', type: 'PowerPoint', icon: FileText },
                { title: 'Attendance Sheet', type: 'PDF', icon: ClipboardList },
                { title: 'Prayer Request Cards', type: 'Printable', icon: BookOpen },
                { title: 'Discussion Question Cards', type: 'Printable', icon: Lightbulb },
                { title: 'Scripture Memory Verses', type: 'PDF', icon: BookMarked },
                { title: 'Group Activity Handouts', type: 'PDF', icon: Users }
              ].map((resource, idx) => (
                <Card key={resource.title} className="opacity-75 cursor-not-allowed">
                  <CardContent className="p-4 text-center">
                    <resource.icon className="w-8 h-8 mx-auto text-rose-500 mb-3" />
                    <p className="font-semibold text-slate-800 text-sm">{resource.title}</p>
                    <Badge variant="outline" className="mt-2 text-xs">{resource.type}</Badge>
                    <Badge variant="outline" className="mt-1 ml-1 text-[10px] text-slate-500">Preview</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'certificates':
        return renderCertificates();

      case 'maps':
        return renderMaps();

      case 'question-bank':
        return renderQuestionBank();

      case 'game-packs':
        return renderGamePacks();

      default:
        return (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {toolboxSections.map(section => (
              <Card 
                key={section.id} 
                className="hover:shadow-lg transition-all cursor-pointer border-2 hover:border-slate-300"
                onClick={() => setActiveTab(section.id)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl ${section.color} flex items-center justify-center`}>
                      <section.icon className="w-6 h-6 text-white" />
                    </div>
                    {section.badge && (
                      <Badge variant="secondary" className="text-xs">
                        {section.badge}
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-bold text-lg text-slate-800 mb-2">{section.title}</h3>
                  <p className="text-sm text-slate-600">{section.description}</p>
                  <div className="mt-4 flex items-center text-sm font-medium text-slate-700">
                    Open <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Verifying instructor access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="flex items-center gap-2 text-slate-700 hover:text-slate-900"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Home</span>
            </Button>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800">Instructor Toolbox</h1>
                <p className="text-xs text-slate-500">Teaching Resources & Tools</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-700">
                <Lock className="w-3 h-3 mr-1" />
                Instructor Access
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8">
        {/* Welcome Banner */}
        {activeTab === 'overview' && (
          <Card className="mb-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">
                    Welcome, {user?.name || 'Instructor'}! 👋
                  </h2>
                  <p className="text-blue-100">
                    Access your maps, question bank, printable game packs, and game setup guides — everything you need to run a session.
                  </p>
                </div>
                <GraduationCap className="w-16 h-16 text-blue-200 hidden md:block" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Breadcrumb / Tab Navigation */}
        {activeTab !== 'overview' && (
          <div className="mb-6">
            <Button 
              variant="ghost" 
              onClick={() => setActiveTab('overview')}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Toolbox Overview
            </Button>
            <h2 className="text-2xl font-bold text-slate-800 mt-4">
              {toolboxSections.find(s => s.id === activeTab)?.title || 'Toolbox'}
            </h2>
          </div>
        )}

        {/* Main Content */}
        {renderContent()}

        {/* Quick Stats - Only on Overview */}
        {activeTab === 'overview' && (
          <Card className="mt-8 bg-slate-50 border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Quick Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-white rounded-lg border">
                  <p className="text-3xl font-bold text-teal-600">{gameMaps.length}</p>
                  <p className="text-sm text-slate-500">Maps & Visual Aids</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border">
                  <p className="text-3xl font-bold text-indigo-600">{bankStats?.total_questions ?? 0}</p>
                  <p className="text-sm text-slate-500">Bank Questions</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border">
                  <p className="text-3xl font-bold text-cyan-600">3</p>
                  <p className="text-sm text-slate-500">Offline Game Files</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border">
                  <p className="text-3xl font-bold text-purple-600">2</p>
                  <p className="text-sm text-slate-500">Print &amp; Play Games</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default InstructorToolbox;
